/**
 * Freshdesk Service API client hook.
 *
 * Mirrors useCrmApiClient for the JSON-based endpoints (currently just /ping
 * for debugging). Adds an SSE streaming method specifically for /api/freshdesk/ask,
 * which returns Server-Sent Events the browser's native EventSource can't consume
 * directly (EventSource only supports GET; ours is POST).
 *
 * Streaming usage:
 *   await client.streamAsk({ question, top_k }, {
 *     onCitations: hits => ...,
 *     onDelta: text => append to UI,
 *     onUsage: u => ...,
 *     onError: err => ...,
 *     onDone: () => ...,
 *   });
 */
import { useCallback, useMemo, useRef } from "react";
import { useMsal } from "@azure/msal-react";

import { useDevBypass } from "../utils/devBypass";
import { useTokenManager } from "./useTokenManager";
import { getConfig } from "../config";

export interface FreshdeskCitation {
  ticket_id: string;
  subject: string;
  type: string | null;
  created_at: string | null;
  tags: string[];
  product_line: string | null;
  score: number;
}

export interface FreshdeskUsage {
  input_tokens: number;
  output_tokens: number;
  cache_creation_input_tokens: number;
  cache_read_input_tokens: number;
  rounds?: number;
}

/** A persisted chat message (text turns only — no tool blocks). */
export interface FreshdeskChatMessage {
  role: "user" | "assistant";
  content: string;
  timestamp: string;
  input_tokens?: number | null;
  output_tokens?: number | null;
}

/** Full thread with its messages (GET /threads/{id}). */
export interface FreshdeskThread {
  id: string;
  user_id: string;
  title: string;
  messages: FreshdeskChatMessage[];
  created_at: string;
  updated_at: string;
  message_count: number;
}

/** Lightweight row for the sidebar list (GET /threads). */
export interface FreshdeskThreadSummary {
  id: string;
  title: string;
  message_count: number;
  created_at: string;
  updated_at: string;
  preview: string | null;
}

/** Agentic event: the model decided to invoke a tool. */
export interface FreshdeskToolCall {
  round: number;
  tool_use_id: string;
  name: string;
  input: Record<string, unknown>;
}

/** Agentic event: the tool returned. `summary` is shaped per tool name. */
export interface FreshdeskToolResult {
  tool_use_id: string;
  summary: {
    ok: boolean;
    error?: string;
    detail?: string;
    row_count?: number;
    truncated?: boolean;
    container?: string;
    hit_count?: number;
    filters?: Record<string, unknown>;
    ticket_id?: string;
    subject?: string;
    body_chars?: number;
  };
}

export interface StreamAskHandlers {
  /** Legacy single-shot citations event — still emitted by the older RAG path
   *  if it's running. Agentic mode emits citations as part of tool_result summaries. */
  onCitations?: (hits: FreshdeskCitation[]) => void;
  onDelta?: (text: string) => void;
  onToolCall?: (call: FreshdeskToolCall) => void;
  onToolResult?: (result: FreshdeskToolResult) => void;
  onUsage?: (u: FreshdeskUsage) => void;
  onError?: (code: string, message: string) => void;
  onDone?: () => void;
}

export interface FreshdeskApiClient {
  /** Diagnostic — proves the service can reach Freshdesk. */
  ping: () => Promise<unknown>;
  /**
   * Stream a /api/freshdesk/ask response. Returns a cancel function that aborts
   * the underlying fetch when the caller wants to walk away.
   */
  streamAsk: (
    body: { question: string; thread_id?: string; top_k?: number },
    handlers: StreamAskHandlers,
  ) => () => void;
  // Thread persistence (PHOTON-29 #10)
  createThread: (initialMessage?: string) => Promise<FreshdeskThread>;
  listThreads: () => Promise<FreshdeskThreadSummary[]>;
  getThread: (id: string) => Promise<FreshdeskThread>;
  renameThread: (id: string, title: string) => Promise<FreshdeskThread>;
  deleteThread: (id: string) => Promise<void>;
  getBaseUrl: () => string;
  isConfigured: () => boolean;
}

/** Parse one fully buffered SSE event ("event: foo\ndata: {...}") into (event, payload). */
function parseSseEvent(raw: string): { event: string; data: unknown } | null {
  const lines = raw.split(/\r?\n/);
  let event = "message";
  const dataLines: string[] = [];
  for (const line of lines) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
  }
  if (dataLines.length === 0) return null;
  const joined = dataLines.join("\n");
  try {
    return { event, data: JSON.parse(joined) };
  } catch {
    return { event, data: joined };
  }
}

export function useFreshdeskApiClient(): FreshdeskApiClient {
  const { instance } = useMsal();
  const tokenManager = useTokenManager();
  const inflight = useRef<AbortController | null>(null);

  const getAuthHeaders = useCallback(async (forceRefresh = false): Promise<HeadersInit> => {
    const token = await tokenManager.getToken(forceRefresh);
    const { userId, email, roles } = tokenManager.getUserInfo();
    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      "X-User-Id": userId,
      "X-User-Email": email,
      "X-User-Roles": roles.join(","),
    };
  }, [tokenManager]);

  const ping = useCallback(async () => {
    const headers = await getAuthHeaders();
    const base = getConfig().freshdeskServiceUrl || "";
    const r = await fetch(`${base}/api/freshdesk/ping`, { headers });
    if (r.status === 401 && !useDevBypass) {
      try { await instance.acquireTokenRedirect({ scopes: ["openid", "profile", "email"] }); } catch {}
      throw new Error("Session expired. Redirecting to login...");
    }
    if (!r.ok) {
      const text = await r.text().catch(() => r.statusText);
      throw new Error(`HTTP ${r.status}: ${text}`);
    }
    return r.json();
  }, [getAuthHeaders, instance]);

  /** Authenticated JSON request against the freshdesk service. */
  const authedJson = useCallback(
    async <T,>(path: string, init?: RequestInit): Promise<T> => {
      const headers = await getAuthHeaders();
      const base = getConfig().freshdeskServiceUrl || "";
      if (!base) throw new Error("freshdeskServiceUrl is not configured");
      const r = await fetch(`${base}${path}`, {
        ...init,
        headers: { ...headers, ...(init?.headers || {}) },
      });
      if (r.status === 401 && !useDevBypass) {
        try { await instance.acquireTokenRedirect({ scopes: ["openid", "profile", "email"] }); } catch {}
        throw new Error("Session expired. Redirecting to login...");
      }
      if (!r.ok) {
        const text = await r.text().catch(() => r.statusText);
        throw new Error(`HTTP ${r.status}: ${text}`);
      }
      if (r.status === 204) return undefined as T;
      return r.json();
    },
    [getAuthHeaders, instance],
  );

  const createThread = useCallback(
    (initialMessage?: string) =>
      authedJson<FreshdeskThread>("/api/freshdesk/threads", {
        method: "POST",
        body: JSON.stringify({ initial_message: initialMessage ?? null }),
      }),
    [authedJson],
  );

  const listThreads = useCallback(
    () => authedJson<FreshdeskThreadSummary[]>("/api/freshdesk/threads"),
    [authedJson],
  );

  const getThread = useCallback(
    (id: string) => authedJson<FreshdeskThread>(`/api/freshdesk/threads/${id}`),
    [authedJson],
  );

  const renameThread = useCallback(
    (id: string, title: string) =>
      authedJson<FreshdeskThread>(`/api/freshdesk/threads/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ title }),
      }),
    [authedJson],
  );

  const deleteThread = useCallback(
    async (id: string) => {
      await authedJson<{ status: string }>(`/api/freshdesk/threads/${id}`, {
        method: "DELETE",
      });
    },
    [authedJson],
  );

  const streamAsk = useCallback(
    (
      body: { question: string; thread_id?: string; top_k?: number },
      handlers: StreamAskHandlers,
    ): (() => void) => {
      // Cancel any prior stream from this hook instance.
      if (inflight.current) {
        inflight.current.abort();
      }
      const controller = new AbortController();
      inflight.current = controller;

      (async () => {
        try {
          const headers = await getAuthHeaders();
          const base = getConfig().freshdeskServiceUrl || "";
          if (!base) {
            handlers.onError?.("NO_BASE_URL", "freshdeskServiceUrl is not configured");
            handlers.onDone?.();
            return;
          }
          const resp = await fetch(`${base}/api/freshdesk/ask`, {
            method: "POST",
            headers,
            body: JSON.stringify(body),
            signal: controller.signal,
          });
          if (resp.status === 401 && !useDevBypass) {
            try { await instance.acquireTokenRedirect({ scopes: ["openid", "profile", "email"] }); } catch {}
            handlers.onError?.("UNAUTHORIZED", "Session expired");
            handlers.onDone?.();
            return;
          }
          if (!resp.ok || !resp.body) {
            const text = await resp.text().catch(() => resp.statusText);
            handlers.onError?.(`HTTP_${resp.status}`, text || `HTTP ${resp.status}`);
            handlers.onDone?.();
            return;
          }

          const reader = resp.body.getReader();
          const decoder = new TextDecoder();
          let buf = "";
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buf += decoder.decode(value, { stream: true });
            // SSE events are delimited by a blank line — "\n\n" or "\r\n\r\n".
            let idx;
            while ((idx = buf.search(/\r?\n\r?\n/)) !== -1) {
              const rawEvent = buf.slice(0, idx);
              const match = buf.slice(idx).match(/^\r?\n\r?\n/);
              buf = buf.slice(idx + (match ? match[0].length : 2));
              const parsed = parseSseEvent(rawEvent);
              if (!parsed) continue;
              const { event, data } = parsed;
              if (event === "citations") {
                handlers.onCitations?.((data as { hits: FreshdeskCitation[] }).hits ?? []);
              } else if (event === "delta") {
                handlers.onDelta?.((data as { text: string }).text ?? "");
              } else if (event === "tool_call") {
                handlers.onToolCall?.(data as FreshdeskToolCall);
              } else if (event === "tool_result") {
                handlers.onToolResult?.(data as FreshdeskToolResult);
              } else if (event === "usage") {
                handlers.onUsage?.(data as FreshdeskUsage);
              } else if (event === "error") {
                const d = data as { code: string; message: string };
                handlers.onError?.(d.code ?? "UNKNOWN", d.message ?? "");
              } else if (event === "done") {
                handlers.onDone?.();
                return;
              }
            }
          }
          handlers.onDone?.();
        } catch (err: unknown) {
          if ((err as { name?: string }).name === "AbortError") return;
          const msg = err instanceof Error ? err.message : String(err);
          handlers.onError?.("STREAM_FAILED", msg);
          handlers.onDone?.();
        } finally {
          if (inflight.current === controller) {
            inflight.current = null;
          }
        }
      })();

      return () => controller.abort();
    },
    [getAuthHeaders, instance],
  );

  // Memoize so consumers get a stable reference across renders — an unstable
  // client object in an effect/callback dependency array causes refetch loops.
  return useMemo(
    () => ({
      ping,
      streamAsk,
      createThread,
      listThreads,
      getThread,
      renameThread,
      deleteThread,
      getBaseUrl: () => getConfig().freshdeskServiceUrl || "",
      isConfigured: () => !!getConfig().freshdeskServiceUrl,
    }),
    [ping, streamAsk, createThread, listThreads, getThread, renameThread, deleteThread],
  );
}

export default useFreshdeskApiClient;
